import React, { useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import {
  X,
  Train,
  Map,
  Stamp,
  Compass,
  Users,
  GraduationCap,
  ShoppingBag,
  LayoutDashboard,
  LogOut,
  User,
} from "lucide-react";

/**
 * MobileNavDrawer — full-screen overlay menu, slide-in from right.
 *
 * Triggered from the hamburger in Navbar. Closed by:
 *  - clicking a nav item
 *  - tapping the X
 *  - tapping the dim backdrop
 *  - pressing Escape
 */
const NAV_ITEMS = [
  { to: "/routes", label: "Tours", icon: Compass, sub: "Eight curated journeys" },
  { to: "/plan-trip", label: "Plan a trip", icon: Train, sub: "Multi-stop, real timetables" },
  { to: "/search", label: "Plan a leg", icon: Train, sub: "Single A → B lookup" },
  { to: "/map", label: "Atlas", icon: Map, sub: "The interactive map" },
  { to: "/groups", label: "Groups", icon: Users, sub: "10-500 travellers" },
  { to: "/schools", label: "Schools", icon: GraduationCap, sub: "Year groups, ABTA-style" },
  { to: "/passport", label: "Passport", icon: Stamp, sub: "Your stamps & XP" },
];

export default function MobileNavDrawer({ open, onClose, user, onLogout, cartCount = 0 }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <div
      data-testid="mobile-nav-drawer"
      aria-hidden={!open}
      className={`fixed inset-0 z-[60] md:hidden transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
    >
      {/* backdrop — solid ink so content behind isn't visible */}
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="absolute inset-0 bg-ink"
      />

      {/* panel — solid navy, hard edge against backdrop */}
      <aside
        className={`absolute top-0 right-0 h-full w-[88%] max-w-[360px] bg-navy border-l border-panel-border shadow-2xl shadow-black/60 transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"} flex flex-col`}
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="flex items-center justify-between p-5 border-b border-panel-border">
          <Link to="/" onClick={onClose} className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center bg-gold text-ink rounded-sm">
              <Train className="h-5 w-5" strokeWidth={2.5} />
            </span>
            <span className="font-display text-xl tracking-[0.18em] text-cream">
              INTER<span className="text-gold">QUEST</span>
            </span>
          </Link>
          <button
            type="button"
            data-testid="mobile-nav-close"
            onClick={onClose}
            aria-label="Close menu"
            className="h-11 w-11 inline-flex items-center justify-center rounded-sm text-cream-dim hover:text-gold hover:bg-panel-border/40 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* user / auth strip */}
        <div className="p-5 border-b border-panel-border">
          {user ? (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display tracking-[0.15em] text-cream text-sm truncate">
                  {user.name || user.email}
                </div>
                <div className="text-gold text-xs font-body mt-1">
                  LVL {user.level} · {user.xp} XP
                </div>
              </div>
              <button
                type="button"
                data-testid="mobile-nav-logout"
                onClick={() => { onLogout(); onClose(); }}
                className="iq-btn-ghost text-sm shrink-0"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Link
                to="/login"
                onClick={onClose}
                data-testid="mobile-nav-login"
                className="iq-btn-ghost justify-center border border-panel-border text-sm py-3"
              >
                <User className="h-4 w-4" /> Sign in
              </Link>
              <Link
                to="/register"
                onClick={onClose}
                data-testid="mobile-nav-register"
                className="iq-btn-secondary justify-center text-sm py-3"
              >
                Join
              </Link>
            </div>
          )}
        </div>

        {/* nav list */}
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV_ITEMS.map(({ to, label, icon: Icon, sub }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-4 px-5 py-4 transition-colors border-l-2 ${
                  isActive
                    ? "border-gold bg-panel-border/30 text-gold"
                    : "border-transparent text-cream hover:bg-panel-border/20"
                }`
              }
            >
              <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
              <span className="min-w-0">
                <span className="block font-display tracking-[0.15em] text-base uppercase">
                  {label}
                </span>
                <span className="block text-cream-dim text-[11px] font-serif italic mt-0.5">
                  {sub}
                </span>
              </span>
            </NavLink>
          ))}

          {user && (
            <NavLink
              to="/dashboard"
              onClick={onClose}
              data-testid="mobile-nav-dashboard"
              className={({ isActive }) =>
                `flex items-center gap-4 px-5 py-4 transition-colors border-l-2 ${
                  isActive
                    ? "border-gold bg-panel-border/30 text-gold"
                    : "border-transparent text-cream hover:bg-panel-border/20"
                }`
              }
            >
              <LayoutDashboard className="h-5 w-5 shrink-0" strokeWidth={1.5} />
              <span>
                <span className="block font-display tracking-[0.15em] text-base uppercase">
                  Dashboard
                </span>
                <span className="block text-cream-dim text-[11px] font-serif italic mt-0.5">
                  Your reservations
                </span>
              </span>
            </NavLink>
          )}
        </nav>

        {/* cart row */}
        <Link
          to="/cart"
          onClick={onClose}
          data-testid="mobile-nav-cart"
          className="flex items-center justify-between gap-3 px-5 py-5 border-t border-panel-border bg-ink/40 hover:bg-panel-border/20 transition-colors"
        >
          <span className="flex items-center gap-3 font-display tracking-[0.15em] text-cream uppercase">
            <ShoppingBag className="h-5 w-5 text-gold" /> Cart
          </span>
          {cartCount > 0 ? (
            <span className="bg-gold text-ink text-xs font-display tracking-wider rounded-full h-6 min-w-6 px-2 inline-flex items-center justify-center">
              {cartCount}
            </span>
          ) : (
            <span className="text-cream-dim text-xs italic font-serif">Empty</span>
          )}
        </Link>

        <p className="px-5 py-3 text-center text-[10px] text-cream-dim/60 font-serif italic border-t border-panel-border/60">
          InterQuest · A romantic atlas for rail wanderers
        </p>
      </aside>
    </div>
  );
}
